// Minimal IndexedDB thumbnail cache with LRU eviction
// Store: 'template-thumbs', objectStore: 'thumbs'
// Value schema: { blob: Blob, mime: string, size: number, lastAccess: number }

const DB_NAME = 'template-thumbs';
const STORE = 'thumbs';
const DB_VERSION = 1;
const MAX_ENTRIES = 200; // tune as needed

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => Promise<T>): Promise<T> {
  const db = await openDB();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    fn(store).then((val) => {
      tx.oncomplete = () => resolve(val);
      tx.onerror = () => reject(tx.error);
    }).catch(reject);
  });
}

export async function getThumb(key: string): Promise<Blob | null> {
  return withStore('readwrite', async (store) => {
    const entry: any = await new Promise((resolve, reject) => {
      const r = store.get(key);
      r.onsuccess = () => resolve(r.result || null);
      r.onerror = () => reject(r.error);
    });
    if (entry && entry.blob) {
      // touch lastAccess
      entry.lastAccess = Date.now();
      await new Promise<void>((resolve, reject) => {
        const r = store.put(entry, key);
        r.onsuccess = () => resolve();
        r.onerror = () => reject(r.error);
      });
      return entry.blob as Blob;
    }
    return null;
  });
}

export async function setThumb(key: string, blob: Blob): Promise<void> {
  await withStore('readwrite', async (store) => {
    const entry = {
      blob,
      mime: blob.type || 'image/png',
      size: blob.size || 0,
      lastAccess: Date.now(),
    };
    await new Promise<void>((resolve, reject) => {
      const r = store.put(entry, key);
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });
    await evictIfNeeded(store);
  });
}

async function evictIfNeeded(store: IDBObjectStore): Promise<void> {
  const keys: string[] = [];
  const entries: { key: string; lastAccess: number }[] = [];
  await new Promise<void>((resolve, reject) => {
    const r = store.openCursor();
    r.onsuccess = () => {
      const cursor = r.result as IDBCursorWithValue | null;
      if (cursor) {
        const key = String(cursor.key);
        const value: any = cursor.value;
        keys.push(key);
        entries.push({ key, lastAccess: (value && value.lastAccess) || 0 });
        cursor.continue();
      } else {
        resolve();
      }
    };
    r.onerror = () => reject(r.error);
  });

  if (keys.length <= MAX_ENTRIES) return;
  // Evict least-recently-used extras
  entries.sort((a, b) => a.lastAccess - b.lastAccess);
  const toEvict = entries.slice(0, keys.length - MAX_ENTRIES);
  await Promise.all(
    toEvict.map(
      (e) =>
        new Promise<void>((resolve, reject) => {
          const del = store.delete(e.key);
          del.onsuccess = () => resolve();
          del.onerror = () => reject(del.error);
        })
    )
  );
}
